import React, {
  Dispatch,
  SetStateAction,
  ReactElement,
  useEffect,
  useState,
  useRef
} from 'react';
import DocViewer, {DocViewerRenderers} from '@cyntler/react-doc-viewer';
import UTIF from 'utif';
import {Buffer} from 'buffer';
import {saveAs} from 'file-saver';
import JSZip from 'jszip';
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef
} from 'react-zoom-pan-pinch';
import {
  DocViewContainer,
  TextContainer,
  ZoomButton,
  AdditionalFeaturesButtonDiv,
  MainContainerDiv,
  PreviewerContainer,
  ZoomButtonsDiv,
  PanToMoveDiv
} from './CustomDocViewerStyles';

import {
  createAuditLog,
  downloadAsPdf,
  getPageData
} from '@shared/services/service-handlers/PrivateService';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import Paper, {PaperProps} from '@mui/material/Paper';
import Draggable from 'react-draggable';
import {
  DocumentScanner,
  Refresh,
  Title,
  ZoomIn,
  ZoomOut
} from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {useTranslation} from 'react-i18next';
import FormConstants from '@shared/constants/FormConstants';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackbarConfig from '@components/snackbar/SnackbarConfig';
import {useSnackbar} from 'notistack';
import TiffRenderer from './CustomRenderers/TiffRenderer';
import RTFRenderer from './CustomRenderers/RTFRenderer';
import TxtRenderer from './CustomRenderers/TxtRenderer';
import {LoadingButton} from '@mui/lab';
import EncryptUtils from 'src/utils/EncryptUtils';
import {useAppContext} from '@context/AppContext';
import EmptyContent from '@components/empty-content/empty-content';
import {
  AI_BLOB_SEARCH,
  BASE_RECORD_ID,
  LOCAL_SEARCH
} from 'src/routes/BaseRoute';
import ReactPDFRenderer from './CustomRenderers/PDFRenderer';
import AuthUtils from 'src/utils/AuthUtils';
import {useModuleContext} from '@context/ModuleContext';
import {generateSubModuleIdentifier} from 'src/utils/helperUtils';
import HTMLRenderer from './CustomRenderers/HTMLRenderer';
import JPEGRenderer from './CustomRenderers/JPEGRenderer';
import {useParams, useLocation, useNavigate} from 'react-router-dom';

interface ActionViewObj {
  actionLabel: string;
  actionFunction: string;
}

interface ActionDownloadObj {
  actionLabel: string;
  actionFunction: string;
  permissions: any;
}

interface CustomDocViewerActions {
  viewActions: ActionViewObj;
  downloadActions: ActionDownloadObj;
}

interface CustomDocViewerProps {
  sectionUIData: any;
  rowData?: any;
  module?: any;
  sub_module?: any;
  // eslint-disable-next-line react/boolean-prop-naming
  refresh?: boolean;
  docDownloadData: any;
  setDocDownloadData: (data: string[]) => void;
  customDocViewerActions: CustomDocViewerActions[];
  height?: any;
  recordId?: string;
  procedure_type?: string;
  specimen_number?: string;
  specimen_number_id?: string;
  procedure_id?: any;
  demographic_id?: any;
  accession_number?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  shoulddisplaymetainfo: boolean;
  setShoulddisplaymetainfo: Dispatch<SetStateAction<boolean>>;
}

// required for zoomIn, zoomOut, and reset functions
interface ControlsProps {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
}

const SingleDocViewer = (props: CustomDocViewerProps): ReactElement => {
  const {
    docDownloadData,
    setDocDownloadData,
    customDocViewerActions,
    height,
    sectionUIData,
    module,
    sub_module,
    refresh,
    recordId,
    shoulddisplaymetainfo,
    setShoulddisplaymetainfo,
    procedure_type,
    specimen_number,
    specimen_number_id,
    procedure_id,
    demographic_id,
    accession_number,
    rowData
  } = props;

  const unSupportedFormats = FormConstants.DOCUMENT_FORMATS.unSupportedFormats;
  const SupportedFormats = FormConstants.DOCUMENT_FORMATS.SupportedFormats;
  const iframeSupportedFormats =
    FormConstants.DOCUMENT_FORMATS.iframeSupportedFormats;

  const theme = useTheme();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [fileExtensions, setFileExtensions] = useState<string[]>([]);
  const [docs, setDocs] = useState<any>([]);
  const [docData, setDocData] = useState<any | null>(null);
  const [activeDocument, setActiveDocument] = useState({uri: ''});
  const [bufferData, setBufferData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [docName, setDocName] = useState<string | null>('download');
  const [textSelectMode, setTextSelectMode] = useState(false);
  const [isMultiPage, setIsMultiPage] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const params: any = useParams();
  const ZoomInShowTextRef = useRef(0);
  // required for zoomIn, zoomOut, and reset functions
  const transformComponentRef = useRef<ReactZoomPanPinchRef>(null);

  const zoomIn = () => {
    transformComponentRef.current?.zoomIn();
  };

  const zoomOut = () => {
    transformComponentRef.current?.zoomOut();
  };

  const resetTransform = () => {
    transformComponentRef.current?.resetTransform();
  };

  const {
    appState: {applicationName}
  } = useAppContext();

  const {
    moduleState: {isBH, isBHReason}
  } = useModuleContext();

  const record_id = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const aiBlobSearch = EncryptUtils.getDecryptedData(
    location?.search,
    AI_BLOB_SEARCH
  );
  const parsedAiBlobSearch = aiBlobSearch ? JSON.parse(aiBlobSearch) : {};

  const localSearch = EncryptUtils.getDecryptedData(
    location?.search,
    LOCAL_SEARCH
  );
  const parsedLocalSearch = localSearch ? JSON.parse(localSearch) : {};

  useEffect(() => {
    if (!isLoading) {
      ZoomInShowTextRef.current = 0;
    }
  }, [ZoomInShowTextRef.current]);

  const getBase64Flag = (extension: string) => {
    if (extension) {
      switch (extension?.toLowerCase()) {
        case 'jpeg':
          return 'data:image/jpeg;base64,';
        case 'jpg':
          return 'data:image/jpg;base64,';
        case 'gif':
          return 'data:image/gif;base64,';
        case 'png':
          return 'data:image/png;base64,';
        case 'bmp':
          return 'data:image/bmp;base64,';
        case 'tiff':
          return 'data:image/tiff;base64,';
        case 'tif':
          return 'data:image/tif;base64,';
        case 'pdf':
          return 'data:application/pdf;base64,';
        case 'txt':
          return 'data:text/txt;base64,';
        case 'rtf':
          return 'data:image/rtf;base64,';
        case 'html':
          return 'data:text/html;base64,';
        case 'htm':
          return 'data:text/html;base64,';
        case 'doc':
          return 'data:application/msword;base64,';
        case 'docx':
          return 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,';
        default:
          return 'data:text/txt;base64,';
      }
    } else {
      return 'data:text/txt;base64,';
    }
  };

  const downloadRTF = (bufferURI: any, fileName: any) => {
    const bufferString = bufferURI.replace(
      /^(data:image\/tiff;base64|data:image\/tif;base64|data:image\/rtf;base64,)/i,
      ''
    );
    const nodeJSBuffer: any = Buffer.from(bufferString, 'base64');
    // Convert the Buffer to a Blob
    const buffer = Buffer.from(nodeJSBuffer);
    const blob = new Blob([buffer], {type: 'application/rtf'});

    // Create a download link
    const downloadLink = document.createElement('a');
    const blobURL = URL.createObjectURL(blob);
    downloadLink.href = blobURL;
    downloadLink.download = fileName || 'output.rtf';

    // Append the link to the document and trigger a click event
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up by removing the link and revoking the blob URL
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(blobURL);
  };

  const getBase64Data = (responseBufferData: any[], file_extensions: any[]) => {

    const base64DocsData: any = [];
    let tempText = '';
    responseBufferData.forEach((eachBuffer: any, index: number) => {
      if (eachBuffer) {
        base64DocsData.push({
          uri: `${getBase64Flag(file_extensions[index])}${Buffer.from(
            eachBuffer.data
          )?.toString('base64')}${tempText}`,
          index: index
        });
        tempText = tempText + ' ';
      }
    });
    setDocs(base64DocsData);
    setActiveDocument(base64DocsData[0]);
    setDocDownloadData(base64DocsData);
  };

  const logDocumentDownload = (dataObj: any) => {
    const documentDetails = bufferData?.[0] ?? dataObj;

    const docDataObj: any = {
      fileName: documentDetails?.name,
      SourceDocumentID: documentDetails?.SourceDocumentID ?? '',
      LegacyMRN: documentDetails?.LegacyMRN,
      ids: documentDetails?.ID,
      downloadType: dataObj.downloadType,
      procedure_type,
      accession_number
    };

    const keyValueString = Object.entries(docDataObj)
      .map(([key, value]) => {
        if (value) return `${key}: ${JSON.stringify(value)}`;
        return null;
      })
      .filter(Boolean)
      .join(', ');
    const data = {
      module: module,
      sub_module: `${sub_module ? sub_module + ' -> ' : ''} ${FormConstants.COMMON.DOWNLOADED_DOCUMENT}`,
      sub_module_identifier: documentDetails?.ID,
      procedure_type,
      application_name: applicationName,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      event_description: keyValueString,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(module, data);
  };

  const getDocumentsData = async () => {
    setIsLoading(true);
    setBufferData([]);
    setActiveDocument({uri: ''});
    setDocDownloadData([]);
    setFileExtensions([]);
    setDocName(null);
    setFileExtensions([]);

    const filePath = rowData?.metadata_storage_path?.replace(
      /^https:\/\/[^/]+\/[^/]+\//,
      ''
    );
    const aiFilePath = parsedAiBlobSearch?.filePath || filePath;
    const finalAiFilePath = aiFilePath?.replaceAll('%20', ' ');
    try {
      const payload: any = {
        document_search_action: true,
        sql_data: sectionUIData?.sql_data,
        record_id: recordId || record_id || rowData?.legacymrn,
        ai_blob_path: finalAiFilePath,
        procedure_type:
          procedure_type ||
          parsedLocalSearch?.data?.row?.ProcedureType ||
          rowData?.ProcedureType,
        submodulePath: rowData?.Path || '',
        accession_number:
          accession_number ||
          parsedLocalSearch?.data?.row?.AccessionNumber ||
          rowData?.AccessionNumber,
        specimen_number: specimen_number,
        audit_sub_module_identifier: generateSubModuleIdentifier(
          module,
          sub_module,
          {
            specimen_number_id,
            procedure_id,
            procedure_type,
            demographic_id,
            ID: recordId || record_id,
            sub_module_identifier_id:
              sectionUIData?.sql_data?.sub_module_identifier_id
          }
        ),
        audit_identifier: AuthUtils.getAuditIdentifier(),
        audit_identifier_type: AuthUtils.getAuditIdentifierType(),
        audit_legacy_app: AuthUtils.getAuditLegacyApp(),
        isBH_selected:
          !isBHReason?.length && isBH == false ? 'true' : String(isBH),
        isBH_reason: isBHReason,
        application_name: applicationName
      };

      const encryptRequestData = EncryptUtils.encryptedData(payload);
      const response = await getPageData(applicationName, module, sub_module, {
        tk: encryptRequestData
      });
      const {data} = response;
      setDocData(data[0]);

      const [singleDocData] = data;
      if (singleDocData?.file_extensions) {
        const permissions = AuthUtils.getPermissions();

        const documentSecurityGroupId =
          singleDocData?.DocumentSecurityGroupID ??
          docData?.DocumentSecurityGroupID;
        const documentSecurityIdExisted = permissions?.find(
          (ele: any) => ele.id === documentSecurityGroupId
        );
        const auditObj = {
          is_audit: true,
          module: module,
          sub_module: `${sub_module ? sub_module + ' -> ' : ''} ${FormConstants.COMMON.VIEWED_DOCUMENT}`,
          sub_module_identifier: singleDocData?.ID,
          procedure_type,
          application_name: applicationName,
          isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
          isBH_reason: isBHReason,
          event_description:
            FormConstants.COMMON.VIEWED_DOCUMENT +
            (documentSecurityIdExisted
              ? `; User has Permission : ${documentSecurityIdExisted.function_label}`
              : ''),
          audit_identifier: AuthUtils.getAuditIdentifier(),
          audit_identifier_type: AuthUtils.getAuditIdentifierType(),
          audit_legacy_app: AuthUtils.getAuditLegacyApp()
        };

        createAuditLog(module, auditObj);
        setBufferData(data);
        setFileExtensions([singleDocData?.file_extensions?.toLowerCase()]);
        getBase64Data(data, [singleDocData?.file_extensions?.toLowerCase()]);
        setDocName(singleDocData?.name);
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      if (err !== 'canceled') {
        const errMessage = getErrorMessage(err);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    }
  };

  useEffect(() => {
    getDocumentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setTextSelectMode(false);
  }, [refresh, location?.search]);

  const isMultiPageTIFF = (base64Data: string): boolean => {
    // Convert base64 to ArrayBuffer
    const byteString = atob(base64Data.split(',')[1]);
    const buffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(buffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }

    // Parse the TIFF file
    const ifds = UTIF.decode(buffer);
    return ifds.length > 1; // Multi-page if more than one IFD (Image File Directory)
  };

  useEffect(() => {
    setIsMultiPage(false);
    if (['tif', 'tiff'].includes(fileExtensions?.[0]?.toLowerCase())) {
      const base64Data = docDownloadData[0]?.uri;
      if (base64Data) {
        const multiPage = isMultiPageTIFF(base64Data);
        setIsMultiPage(multiPage);
      }
    }
  }, [docDownloadData, fileExtensions]);

  const downloadFile = async (
    downloadData: any[],
    fileExtensions: string[],
    fileName: string | null
  ) => {
    setIsDownloading(true);
    logDocumentDownload({downloadType: 'Downloaded Original'});

    if (downloadData.length > 0) {
      if (downloadData?.length === 1) {
        if (
          ['rtf', 'tiff', 'tif'].includes(fileExtensions?.[0]?.toLowerCase())
        ) {
          downloadRTF(
            downloadData?.[0]?.uri,
            `${fileName}.${fileExtensions?.[0]?.toLowerCase()}`
          );
        } else {
          const a = document.createElement('a');
          a.setAttribute('href', `${downloadData?.[0]?.uri}`);
          a.setAttribute(
            'download',
            `${fileName}.${fileExtensions[0]?.toLowerCase()}`
          );
          a.click();
        }
      } else {
        const zip = new JSZip();
        downloadData.map((item: any, index: any) => {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const [fileFlag, fileData] = item?.uri?.split(',');
          const fileExtension = fileFlag
            ?.split('/')?.[1]
            ?.split(';')?.[0]
            ?.toLowerCase();

          const invalidCharacters = /[\\/:*?"<>|]/g;
          const sanitizedFileName = fileName?.replace(invalidCharacters, '_');
          zip.file(`${sanitizedFileName}_${index}.${fileExtension}`, fileData, {
            base64: true
          });
        });
        zip.generateAsync({type: 'blob'}).then(function (content) {
          saveAs(content, `${fileName}.zip`);
        });
      }
    }
    setIsDownloading(false);
  };

  const downloadAllPages = async (
    isMultiPage: boolean,
    downloadData: any[],
    fileExtensions: string[],
    fileName: string | null
  ) => {
    if (!isMultiPage) {
      // If it's not multi-page, fallback to the standard download
      downloadFile(downloadData, fileExtensions, fileName);
      return;
    }
    setIsDownloading(true);
    const base64Data = docDownloadData[0]?.uri;

    if (base64Data) {
      const zip = new JSZip();

      // Convert base64 to ArrayBuffer
      const byteString = atob(base64Data.split(',')[1]);
      const buffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(buffer);
      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
      }

      // Decode this file and process each page
      const ifds = UTIF.decode(buffer);

      for (let i = 0; i < ifds.length; i++) {
        UTIF.decodeImage(buffer, ifds[i]);
        const rgba = UTIF.toRGBA8(ifds[i]);
        const width = ifds[i].width;
        const height = ifds[i].height;

        // Create canvas to render file
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        // Draw this page as ImageData
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to Original Blob and add to ZIP file
        const blob = await new Promise<Blob | null>(resolve =>
          canvas.toBlob(resolve, `image/${fileExtensions}`)
        );
        const invalidCharacters = /[\\/:*?"<>|]/g;
        const sanitizedFileName = fileName?.replace(invalidCharacters, '_');
        if (blob) {
          zip.file(`${sanitizedFileName}_${i + 1}.${fileExtensions}`, blob);
        }
      }

      // Generate ZIP file and trigger download
      zip.generateAsync({type: 'blob'}).then(function (content) {
        saveAs(content, `${fileName}.zip`);
      });
    }
    setIsDownloading(false);
  };

  // Sends Document Details, instead of data stream
  const downloadFileAsPDF = async (
    version?: string,
    versionID?: any,
    updatedDocData?: any
  ) => {
    setIsDownloading(true);
   const filePath = rowData?.metadata_storage_path?.replace(
      /^https:\/\/[^/]+\/[^/]+\//,
      ''
    );

    const documentDetails = bufferData?.[0];

    const firstElem: any = documentDetails?.ID;

    const data: any = {
      SourceDocumentID: documentDetails?.SourceDocumentID ?? '',
      versions: '',
      versionData: '',
      sql: sectionUIData?.sql_data?.sql,
      variables: [
        {data_column2: 'A.ID', req_column: 'ID'},
        ...(sectionUIData?.sql_data?.variables ?? {})
      ],
      ids: [documentDetails?.ID],
      filterSchemaClause: sectionUIData?.sql_data?.filter_schema_clause,
      audit_sub_module_identifier: firstElem,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp(),
      version: version ? version : '1',
      legacyMRN: documentDetails?.LegacyMRN ?? recordId,
      procedure_type,
      ai_blob_path: parsedAiBlobSearch?.filePath || filePath,

      submodulePath: rowData?.Path || '',
      accession_number
    };

    data.sql_data = sectionUIData?.sql_data;
    data.module = params.module_name;
    data.submodule_name = params.submodule_name;


    try {
      const encryptedData = EncryptUtils.encryptedData(data);
      const response = await downloadAsPdf({tk: encryptedData});
      logDocumentDownload({downloadType: 'Download as PDF'});
      const url = window.URL.createObjectURL(
        new Blob([response], {type: 'application/pdf'})
      );

      // Create a temporary anchor element and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${documentDetails?.name}.pdf`); // The file name
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setIsLoading(false);
      if (err) {
        const errMessage = getErrorMessage(err);
        console.error('Error during file download:', errMessage);
        enqueueSnackbar(errMessage, SnackbarConfig.getError());
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDocumentChange = (document: any) => {
    setActiveDocument(document);
  };

  const renderPreviewer = (): any => {
    let initialPreviewer: any;

    fileExtensions.forEach(eachExtension => {
      if (!SupportedFormats.includes(eachExtension.toUpperCase())) {
        initialPreviewer = (
          <TextContainer
            height={height}
            width={'min-content'}
            maxWidth={'50vw'}>
            <Typography variant="h6">
              {t('PRIVATE.DOCUMENT.NO_PREVIEW_AVAILABLE', {
                ns: 'private'
              })}
            </Typography>
          </TextContainer>
        );
      }
    });

    if (!initialPreviewer) {
      if (
        !iframeSupportedFormats.includes(fileExtensions[0]?.toLowerCase()) &&
        typeof activeDocument?.uri === 'string' &&
        docs?.length
      ) {
        initialPreviewer = (
          <PreviewerContainer>
            <DocViewer
              documents={docs}
              pluginRenderers={[
                ...DocViewerRenderers,
                JPEGRenderer,
                HTMLRenderer,
                TiffRenderer,
                RTFRenderer,
                TxtRenderer,
                ReactPDFRenderer
              ]}
              initialActiveDocument={activeDocument}
              activeDocument={activeDocument}
              onDocumentChange={handleDocumentChange}
              config={{
                header: {
                  disableFileName: true
                },
                pdfVerticalScrollByDefault: true
              }}
              style={{
                paddingBottom: '3rem',
                width: '100%',
                height: '100%',
                minWidth: '45vw',
                minHeight: '90vh'
              }}
            />
          </PreviewerContainer>
        );
      }
    }
    return initialPreviewer;
  };

  const getPreviewer = React.useMemo(() => {
    return (
      <Box>
        {textSelectMode ? (
          <MainContainerDiv
            style={{
              display: 'flex',
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'start',
              marginLeft: '10px',
              overflow: 'hidden',
              minWidth: '53vw',
              minHeight: '60vh'
            }}>
            {renderPreviewer()}
          </MainContainerDiv>
        ) : (
          activeDocument?.uri && (
            <TransformWrapper
              ref={transformComponentRef}
              initialScale={1}
              initialPositionX={0}
              initialPositionY={0}
              minScale={0.5}
              maxScale={10}
              limitToBounds={true}
              wheel={{
                disabled: true,
                wheelDisabled: true,
                touchPadDisabled: true
              }}>
              {({...rest}) => (
                <MainContainerDiv>
                  <TransformComponent
                    contentStyle={{
                      display: 'flex',
                      position: 'relative',
                      alignItems: 'flex-start',
                      justifyContent: 'start',
                      marginLeft: '10px',
                      overflow: 'hidden',
                      minWidth: '53vw',
                      minHeight: '60vh'
                    }}>
                    {renderPreviewer()}
                  </TransformComponent>
                </MainContainerDiv>
              )}
            </TransformWrapper>
          )
        )}
      </Box>
    );
  }, [textSelectMode, activeDocument, docs]);

  const viewDocumentAction = React.useCallback((): ReactElement => {
    return (
      <Stack
        sx={{
          px: 3
        }}>
        <LoadingButton
          type="submit"
          variant={shoulddisplaymetainfo ? 'outlined' : 'contained'}
          onClick={() => setShoulddisplaymetainfo(!shoulddisplaymetainfo)}
          sx={{
            bgcolor: theme.palette.primary.main
          }}>
          {shoulddisplaymetainfo
            ? t('PRIVATE.DOCUMENT.HIDE_DETAILS', {
                ns: 'private'
              })
            : t('PRIVATE.DOCUMENT.VIEW_DETAILS', {
                ns: 'private'
              })}
        </LoadingButton>
      </Stack>
    );
  }, [shoulddisplaymetainfo, theme]);

  const downloadDocumentAction = React.useCallback((): ReactElement => {
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleMenuItemClick = (option: string) => {
      if (option === 'Download As PDF') {
        downloadFileAsPDF();
      } else {
        isMultiPage
          ? downloadAllPages(
              isMultiPage,
              docDownloadData,
              fileExtensions,
              docName
            )
          : downloadFile(docDownloadData, fileExtensions, docName);
      }
      handleClose();
    };

    const options = ['Download Original', 'Download As PDF'];

    return (
      <Stack direction="row" spacing={2} sx={{pr: 3}}>
        <ButtonGroup variant="contained">
          <LoadingButton
            loading={isDownloading}
            sx={{
              bgcolor: theme.palette.primary.dark,
              color: 'white',
              borderTopLeftRadius: 'inherit',
              borderBottomLeftRadius: 'inherit',
              '&:hover': {bgcolor: theme.palette.primary.main},
              paddingLeft: '16px'
            }}
            onClick={() =>
              isMultiPage
                ? downloadAllPages(
                    isMultiPage,
                    docDownloadData,
                    fileExtensions,
                    docName
                  )
                : downloadFile(docDownloadData, fileExtensions, docName)
            }>
            Download
          </LoadingButton>
          <Button
            disabled={isDownloading}
            size="small"
            sx={{
              bgcolor: theme.palette.primary.dark,
              color: 'white',
              borderTopRightRadius: 'inherit !important',
              borderBottomRightRadius: 'inherit !important',
              height: 'auto !important',
              minWidth: 'auto',
              '&:hover': {
                bgcolor: theme.palette.primary.main
              }
            }}
            aria-controls={anchorEl ? 'split-button-menu' : undefined}
            // eslint-disable-next-line no-extra-boolean-cast
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
            aria-haspopup="menu"
            onClick={handleClick}>
            <ArrowDropDownIcon />
          </Button>
          <Menu
            id="split-button-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}>
            {options.map(option => (
              <MenuItem
                key={option}
                onClick={() => handleMenuItemClick(option)}>
                {option}
              </MenuItem>
            ))}
          </Menu>
        </ButtonGroup>
      </Stack>
    );
  }, [
    isDownloading,
    anchorEl,
    docDownloadData,
    fileExtensions,
    docName,
    isMultiPage
  ]);

  useEffect(() => {
    const docsList: any = [];
    const docsListTemp: any = [];
    bufferData.forEach((eachBuffer: any, index: number) => {
      if (!unSupportedFormats.includes(fileExtensions[index])) {
        docsList.push({
          uri: `${getBase64Flag(fileExtensions[index])}${Buffer.from(
            eachBuffer.data
          )?.toString('base64')}`,
          index: index
        });
        docsListTemp.push(index);
      }
    });
    setActiveDocument(docsList[0]);
  }, [bufferData, fileExtensions]);

  useEffect(() => {
    getPreviewer;
  }, [docs, activeDocument]);

  const Controls: React.FC<ControlsProps> = ({
    zoomIn,
    zoomOut,
    resetTransform
  }) => {
    return (
      <ZoomButtonsDiv className="tools">
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.ZOOM_IN', {
              ns: 'private'
            })}
            onClick={zoomIn}>
            <ZoomIn
              sx={{
                color: theme.palette.common.white
              }}
            />
          </ZoomButton>
        )}
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.ZOOM_OUT', {
              ns: 'private'
            })}
            onClick={zoomOut}>
            <ZoomOut
              sx={{
                color: theme.palette.common.white
              }}
            />
          </ZoomButton>
        )}
        {!fileExtensions[0]?.toLowerCase().includes('txt') && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.RESET', {
              ns: 'private'
            })}
            onClick={resetTransform}>
            <Refresh
              sx={{
                color: theme.palette.common.white
              }}
            />
          </ZoomButton>
        )}
        {(!fileExtensions[0]?.toLowerCase().includes('txt') &&
          !textSelectMode &&
          ZoomInShowTextRef.current > 0) ||
        ZoomInShowTextRef.current < 0 ? (
          <PanToMoveDiv>
            {t('PRIVATE.DOCUMENT.MOVE_DOCUMENT', {
              ns: 'private'
            })}
          </PanToMoveDiv>
        ) : null}
        {['pdf', 'rtf'].includes(fileExtensions[0]?.toLowerCase()) && (
          <ZoomButton
            title={t('PRIVATE.DOCUMENT.RESET', {
              ns: 'private'
            })}
            onClick={() => {
              setTextSelectMode(prev => !prev);
            }}>
            {textSelectMode ? (
              <DocumentScanner
                sx={{
                  color: theme.palette.common.white
                }}
              />
            ) : (
              <Title
                sx={{
                  color: theme.palette.common.white
                }}
              />
            )}
          </ZoomButton>
        )}
      </ZoomButtonsDiv>
    );
  };

  const renderButtons = React.useCallback((): ReactElement => {
    return (
      <AdditionalFeaturesButtonDiv>
        {customDocViewerActions.map((item: any) => {
          if (item.action_name === FormConstants.DOCUMENTS_ACTION_TYPES.VIEW) {
            return viewDocumentAction();
          } else if (
            item.action_name ===
              FormConstants.DOCUMENTS_ACTION_TYPES.DOWNLOAD &&
            docDownloadData?.length > 0
          ) {
            if (specimen_number) {
              return null;
            }
            return downloadDocumentAction();
          } else {
            return null;
          }
        })}
        <Controls
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          resetTransform={resetTransform}
        />
      </AdditionalFeaturesButtonDiv>
    );
  }, [
    customDocViewerActions,
    docDownloadData,
    zoomIn,
    zoomOut,
    resetTransform
  ]);

  function PaperComponent(props: PaperProps) {
    return (
      <Draggable
        handle="#draggable-dialog-title"
        cancel={'[class*="MuiDialogContent-root"]'}>
        <Paper {...props} />
      </Draggable>
    );
  }

  const handleClose = () => {
    setShoulddisplaymetainfo(false);
  };

  return isLoading ? (
    <DocViewContainer isMultiPage={fileExtensions?.length > 1} height={height}>
      <ComponentLoader loaderSize={10} />
    </DocViewContainer>
  ) : bufferData.length === 0 ? (
    <Box sx={{marginBottom: 10}}>
      <EmptyContent
        title="No file Found"
        description="There are no files available at the moment."
        imgUrl="/assets/icons/empty/ic_email_selected.svg"
        sx={{
          borderRadius: 1.5,
          bgcolor: 'background.default'
        }}
      />
    </Box>
  ) : (
    <Stack
      direction={'column'}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%'
      }}>
      <Box
        sx={{
          padding: 2
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}>
          <Box>{renderButtons()}</Box>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          top: '0',
          width: '100%',
          overflowY: 'scroll'
        }}>
        <DocViewContainer
          isMultiPage={fileExtensions?.length > 1}
          height={height}>
          {getPreviewer}
        </DocViewContainer>
      </Box>

      {shoulddisplaymetainfo && docData && (
        <Dialog
          open={shoulddisplaymetainfo}
          onClose={handleClose}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title">
          <DialogTitle style={{cursor: 'move'}} id="draggable-dialog-title">
            Document Details
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {docData &&
                Object.entries(docData)
                  .filter(
                    ([key]) =>
                      ![
                        'data',
                        'name',
                        'file_extensions',
                        'ID',
                        'Sequence'
                      ].includes(key)
                  )
                  .map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="subtitle2" component="span" mr={1}>
                        {key}:
                      </Typography>
                      <Typography variant="body2" component="span">
                        {value ? value.toString() : '-'}
                      </Typography>
                    </Box>
                  ))}
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <LoadingButton
              type="reset"
              variant="outlined"
              onClick={handleClose}
              sx={{
                backgroundColor: theme.palette.primary.light
              }}>
              {t('PRIVATE.COMMON.CLOSE', {ns: 'private'})}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
};

export default SingleDocViewer;
